if (!customElements.get('custom-select-me')) {
  class CustomSelectMe extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });

      const style = document.createElement('style');
      style.textContent = `
        .select-container {
          position: relative;
          width: 100%;
        }
        .selected {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-weight: 400;
          border: 1px solid #ccc;
          border-radius: 8px;
          background-color: white;
          font-size: 1em;
          user-select: none;
        }
        .selected:hover, .selected:focus {
          border: 1px solid #333333;
        }
        .selected.error {
          border: 1px solid #e53935 !important;
          background-color: #fff0f0;
        }
        .select-input {
          padding: 14px 12px;
          border: none;
          border-radius: 8px;
          font-size: 1em;
          width: 100%;
          line-height: 1.2em;
          background: transparent;
          flex: 1;
        }
        .select-input:focus {
          outline: none;
        }
        .arrow {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          margin-right: 12px;
          transition: transform 0.3s ease;
        }
        .open .arrow {
          transform: rotate(180deg);
        }
        .clear-btn {
          display: none;
          align-items: center;
          justify-content: center;
          margin-right: 6px;
          cursor: pointer;
          border-radius: 50%;
        }
        .clear-btn:hover {
          background-color: #dfdfdf;
        }
        .clear-btn svg {
          width: 21px;
          height: auto;
          pointer-events: none;
        }
        .options {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
          background: white;
          display: none;
          z-index: 10;
          border-radius: 0 0 8px 8px;
          max-height: 300px;
          overflow-y: auto;
        }
        .open .options {
          display: block;
        }
        .option {
          padding: 15px 25px;
          cursor: pointer;
          color: #999;
        }
        .option:hover {
          background-color: #fafafa;
          color: #333;
          font-weight: 500;
        }
        .no-match {
          padding: 15px 25px;
          color: #ccc;
          text-align: center;
          font-style: italic;
        }
      `;

      this.container = document.createElement('div');
      this.container.classList.add('select-container');
      this.container.innerHTML = `
        <div class="selected">
          <input class="select-input" placeholder="${this.getAttribute('placeholder') || 'Search or select'}" />
          <span class="clear-btn" title="Clear selection">
            <svg width="21px" height="21px" viewBox="0 0 21 21">
              <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                <g transform="translate(-1551.000000, -171.000000)">
                  <g transform="translate(1551.000000, 171.000000)">
                    <rect x="0" y="0" width="21" height="21"></rect>
                    <line x1="14.3" y1="7" x2="7" y2="14.3" stroke="#333333" stroke-linecap="round" stroke-linejoin="round"></line>
                    <line x1="14.3" y1="7" x2="7" y2="14.3" stroke="#333333" stroke-linecap="round" stroke-linejoin="round" transform="translate(10.65, 10.65) scale(-1, 1) translate(-10.65, -10.65)"></line>
                  </g>
                </g>
              </g>
            </svg>
          </span>
          <span class="arrow">
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path d="M20 8.5 12.5 16 5 8.5" stroke="currentColor" stroke-width="1.5" fill="none"/>
            </svg>
          </span>
        </div>
        <div class="options"></div>
      `;

      this.shadowRoot.append(style, this.container);
    }

    connectedCallback() {
      const input = this.container.querySelector('.select-input');
      const optionsDiv = this.container.querySelector('.options');
      const selectedDiv = this.container.querySelector('.selected');
      const clearBtn = this.container.querySelector('.clear-btn');
      const options = Array.from(this.querySelectorAll('option'));

      const updateClearBtnVisibility = () => {
        clearBtn.style.display = this.getAttribute('value') ? 'inline-flex' : 'none';
      };

      const renderOptions = (filter = '') => {
        const allFlag = this.getAttribute('data-all-flag') === 'true';
        if (filter === 'All' && allFlag) filter = '';
        optionsDiv.innerHTML = '';

        const filtered = options.filter(opt =>
          opt.textContent.toLowerCase().includes(filter.toLowerCase())
        );

        if (filtered.length === 0) {
          const noMatch = document.createElement('div');
          noMatch.className = 'no-match';
          noMatch.textContent = "No matching options";
          optionsDiv.appendChild(noMatch);
          return;
        }

        filtered.forEach(opt => {
          const div = document.createElement('div');
          div.className = 'option';
          div.textContent = opt.textContent;
          div.dataset.value = opt.value;

          div.addEventListener('mousedown', () => {
            input.value = opt.textContent;
            this.setAttribute('value', opt.value);
            this.container.classList.remove('open');
            updateClearBtnVisibility();

            this.dispatchEvent(new Event('change'));
            this.dispatchEvent(new CustomEvent('option-change', {
              detail: {
                value: opt.value,
                label: opt.textContent
              },
              bubbles: true,
              composed: true
            }));
          });

          optionsDiv.appendChild(div);
        });
      };

      input.addEventListener('focus', () => {
        this.container.classList.add('open');
        const val = input.value.trim().toLowerCase();
        const allFlag = this.getAttribute('data-all-flag') === 'true';
        if (allFlag && val === 'all') {
          input.value = '';
          renderOptions('');
        } else {
          renderOptions(input.value);
        }
      });

      selectedDiv.addEventListener('pointerdown', (e) => {
        if (e.target !== input) {
          e.preventDefault();
          setTimeout(() => input.focus(), 0);
        }
      });

      input.addEventListener('input', () => {
        renderOptions(input.value);
        updateClearBtnVisibility();
      });

      input.addEventListener('blur', () => {
        setTimeout(() => {
          const inputVal = input.value.trim().toLowerCase();
          const matched = options.some(opt => opt.textContent.toLowerCase() === inputVal);
          const allFlag = this.getAttribute('data-all-flag') === 'true';
          this.container.classList.remove('open');

          if (!matched) {
            if (allFlag) {
              input.value = 'All';
              this.removeAttribute('value');
              this.dispatchEvent(new CustomEvent('option-change', {
                detail: { value: '', label: 'All' },
                bubbles: true,
                composed: true
              }));
            } else {
              input.value = '';
              this.removeAttribute('value');
              this.dispatchEvent(new CustomEvent('option-change', {
                detail: { value: '', label: '' },
                bubbles: true,
                composed: true
              }));
            }
            updateClearBtnVisibility();
          }
        }, 150);
      });

      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const allFlag = this.getAttribute('data-all-flag') === 'true';
      
        if (allFlag) {
          input.value = 'All';
          this.removeAttribute('value');
          this.dispatchEvent(new CustomEvent('option-change', {
            detail: { value: '', label: 'All' },
            bubbles: true,
            composed: true
          }));
        } else {
          input.value = '';
          this.removeAttribute('value');
          this.dispatchEvent(new CustomEvent('option-change', {
            detail: { value: '', label: '' },
            bubbles: true,
            composed: true
          }));
        }
      
        updateClearBtnVisibility();
      
        // 👇 新增：立即刷新选项列表为“全部”
        renderOptions('');
      });

      // 初始化时显示 All 并触发事件
      if (!this.getAttribute('value') && this.getAttribute('data-all-flag') === 'true') {
        input.value = 'All';
        this.dispatchEvent(new CustomEvent('option-change', {
          detail: { value: '', label: 'All' },
          bubbles: true,
          composed: true
        }));
      }

      renderOptions('');
      updateClearBtnVisibility();
    }

    setErrorState(hasError) {
      const selectedEl = this.container.querySelector('.selected');
      if (selectedEl) selectedEl.classList.toggle('error', hasError);
    }

    get value() {
      return this.getAttribute('value');
    }

    set value(val) {
      const input = this.container.querySelector('.select-input');
      const allFlag = this.getAttribute('data-all-flag') === 'true';

      if (!val && allFlag) {
        input.value = 'All';
        this.removeAttribute('value');
        return;
      }

      const opt = Array.from(this.querySelectorAll('option')).find(o => o.value === val);
      if (opt) {
        input.value = opt.textContent;
        this.setAttribute('value', val);
      } else {
        input.value = '';
        this.removeAttribute('value');
      }
    }
  }

  customElements.define('custom-select-me', CustomSelectMe);
}
